import React, { useEffect, useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Stack,
  Button,
  Typography,
  Grid,
  Card,
  Paper,
  TextField,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableFooter,
  Pagination,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  DialogActions,
  DialogContentText,
  Tabs,
  Tab,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import { toast } from 'react-toastify';
import Iconify from 'src/components/iconify';
import ReactApexChart from 'react-apexcharts';
import { useSelector } from 'react-redux';
import Services from 'src/services/Services';
import api from 'src/services/BaseService';
import HeatmapLegend from 'src/utils/mallManagement/HeatMapLegend';
import { SERVICES } from 'src/utils/servicePermissions';
import useServicePermission from 'src/hooks/useServicePermission';

const formatDate = (value) => moment(value).format('DD.MM.YYYY - HH:mm');
const DAY_MAP_TR = { "Monday": "P.tesi", "Tuesday": "Salı", "Wednesday": "Çarş.", "Thursday": "Perş.", "Friday": "Cuma", "Saturday": "C.tesi", "Sunday": "Pazar" };
const DAY_ORDER = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const calculateDuration = (start, end) => {
  if (!start || !end) return '-';
  const duration = moment.duration(moment(end).diff(moment(start)));
  const hours = Math.floor(duration.asHours());
  const minutes = duration.minutes();
  if (hours > 0) return `${hours} Saat ${minutes} Dakika`;
  return `${minutes} Dakika`;
};

export default function MallManagementSafetyCloakroom() {
  const parsedUser = JSON.parse(localStorage.getItem('user'));
  const customer = useSelector((state) => state.customerReducer);
  const mallID = customer?.customer?.id;
  const theme = useTheme();

  const [analyticsData, setAnalyticsData] = useState(null);
  const [dropboxList, setDropboxList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingList, setLoadingList] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [hasPermission, setHasPermission] = useState(false);
  const [showApplyBanner, setShowApplyBanner] = useState(false);
  const [openApplyDialog, setOpenApplyDialog] = useState(false);

  const [priceInfo, setPriceInfo] = useState([]);
  const [loadingPrices, setLoadingPrices] = useState(true);
  const [activePriceTab, setActivePriceTab] = useState(0);

  const { hasPermission: safetyCloakroomPermission, loading: permissionLoading } = useServicePermission(SERVICES.SAFETY_CLOAKROOM);

  const [filterText, setFilterText] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [heatmapDataType, setHeatmapDataType] = useState('usageCount');
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;
  const [isVideoModalOpen, setVideoModalOpen] = useState(false);

  const handleOpenVideoModal = () => setVideoModalOpen(true);
  const handleCloseVideoModal = () => setVideoModalOpen(false);

  const handleApplyClick = () => setOpenApplyDialog(true);
  const handleCloseDialog = () => setOpenApplyDialog(false);
  const handleConfirmApply = async () => {
    try {
      const mallsResponse = await api.post('/malls/get-malls', { withOffices: true });

      if (mallsResponse.data && mallsResponse.data.status) {
        const foundMall = mallsResponse.data.result.find((item) => item.id === mallID);
        const mallTitle = foundMall?.title || 'Bilinmeyen AVM';

        const formData = {
          mallName: mallTitle,
          mallID: mallID,
          userName: parsedUser?.name || parsedUser?.email || 'Bilinmeyen Kullanıcı',
          serviceName: 'Emanet Vestiyer',
        };

        const response = await Services.createContactForm(parsedUser.token, formData);

        if (response.data && response.data.status) {
          toast.success('Talebiniz başarıyla iletilmiştir.');
          handleCloseDialog();
        } else {
          toast.error(response.data?.message || 'Talep gönderilirken bir hata oluştu.');
        }
      } else {
        toast.error('AVM bilgileri alınamadı.');
      }
    } catch (error) {
      console.error('Error submitting service request:', error);
      toast.error('Talep gönderilirken bir hata oluştu.');
    }
  };

  const fetchPriceInfo = React.useCallback(async () => {
    if (!parsedUser?.token || !mallID) {
      setLoadingPrices(false);
      return;
    }

    setLoadingPrices(true);
    try {
      const mallInfoResponse = await Services.GetMallInformation(parsedUser.token, mallID);
      if (!mallInfoResponse.data.status || !mallInfoResponse.data.result.checkroomList) {
        setPriceInfo([]);
        setLoadingPrices(false);
        return;
      }
      const checkroomList = mallInfoResponse.data.result.checkroomList;
      if (checkroomList.length === 0) {
        setPriceInfo([]);
        setLoadingPrices(false);
        return;
      }
      const locationSettingsPromises = checkroomList.map(locationCode =>
        Services.GetAllCheckroomPrices(locationCode)
      );
      const locationSettingsResponses = await Promise.all(locationSettingsPromises);
      const processedPriceInfo = locationSettingsResponses
        .filter(res => res.data.status && Array.isArray(res.data.result))
        .map(res => {
          const priceList = res.data.result;
          if (priceList.length === 0) {
            return null;
          }
          const locationCode = priceList[0].locationCode;
          const activePrices = priceList
            .filter(d => d.price != null)
            .map(d => ({
              time: d.minute / 60,
              price: d.price
            }));

          return {
            locationCode: locationCode,
            locationName: null,
            prices: activePrices
          };
        })
        .filter(Boolean);
      setPriceInfo(processedPriceInfo);

    } catch (error) {
      console.error("Fiyat bilgisi alınırken hata:", error);
      toast.error('Fiyat bilgileri alınamadı.');
      setPriceInfo([]);
    } finally {
      setLoadingPrices(false);
    }
  }, [parsedUser?.token, mallID]);

  useEffect(() => {
    if (permissionLoading) {
      return;
    }

    const getLiveAnalytics = async () => {
      if (!parsedUser?.token || !mallID) {
        setLoading(false);
        setLoadingPrices(false);
        return;
      }
      setLoading(true);
      try {
        const userHasPermission = safetyCloakroomPermission;
        setHasPermission(userHasPermission);

        if (userHasPermission) {
          const analyticsPromise = Services.GetLiveAnalyticsCheckRoom(parsedUser.token, mallID);
          const pricePromise = fetchPriceInfo();

          const [analyticsResponse] = await Promise.all([
            analyticsPromise,
            pricePromise
          ]);

          setAnalyticsData(analyticsResponse.data.result || {});
          await getLiveAnalyticsDropboxList(true, null, null);

        } else {
          setAnalyticsData({});
          setDropboxList([]);
          setShowApplyBanner(true);
          setPriceInfo([]);
          setLoadingPrices(false);
        }
      } catch (err) {
        console.error('Error fetching analytics:', err);
        setAnalyticsData({});
        setDropboxList([]);
        setPriceInfo([]);
        setLoadingPrices(false);
      } finally {
        setLoading(false);
      }
    };
    getLiveAnalytics();
  }, [mallID, parsedUser?.token, safetyCloakroomPermission, permissionLoading, fetchPriceInfo]);

  const getLiveAnalyticsDropboxList = async (isInitial = false, start, end) => {
    if (!hasPermission && !isInitial) return;
    if (!parsedUser?.token || !mallID) return;
    setLoadingList(true);
    try {
      const response = await Services.GetLiveAnalyticsCheckRoomList(parsedUser.token, mallID, start, end);
      const result = response.data.result || {};
      const flatList = Object.entries(result).flatMap(([locationCode, entries]) => entries.map(entry => ({ ...entry, locationCode })));
      setDropboxList(flatList);
    } catch (err) {
      console.error('Error fetching dropbox list:', err);
    } finally {
      setLoadingList(false);
    }
  };

  const handleFilter = () => {
    setPage(1);
    getLiveAnalyticsDropboxList(false, startDate, endDate);
  };

  const handleClearFilter = () => {
    setFilterText('');
    setStartDate(null);
    setEndDate(null);
    setPage(1);
    getLiveAnalyticsDropboxList(false, null, null);
  };

  const handleExportToExcel = () => {
    if (filteredDropboxList.length === 0) { toast.warn('Dışa aktarılacak veri bulunmuyor.'); return; }
    setExporting(true);
    const headerRow = ['Lokasyon Kodu', 'Kullanım Süresi', 'Başlangıç Tarihi', 'Bitiş Tarihi', 'Vestiyer No', 'Ücret (₺)'];
    const dataRows = filteredDropboxList.map(item => [item.locationCode || '-', calculateDuration(item.startDate, item.endDate), item.startDate ? formatDate(item.startDate) : '-', item.endDate ? formatDate(item.endDate) : '-', item.checkRoomNumber || '-', item.price !== undefined ? `${item.price} ₺` : '-']);
    const finalContent = [['Vestiyer İşlemleri'], [], headerRow, ...dataRows];
    api.post('excel/export', { type: 'excel', content: finalContent, sheetTitle: 'Vestiyer Raporu' })
      .then(response => { if (response.data.status) { window.open(response.data.result, '_blank'); } else { toast.error(response.data.message); } })
      .catch(() => toast.error('Excel dosyası oluşturulurken bir hata oluştu.'))
      .finally(() => setExporting(false));
  };

  const zeroOutSeries = (series) => {
    if (!Array.isArray(series)) return [];
    const firstEl = series[0];
    if (typeof firstEl === 'number') { return series.map(() => 0); }
    if (typeof firstEl === 'object' && firstEl !== null && 'data' in firstEl) {
      return series.map(s => ({ ...s, data: Array.isArray(s.data) ? s.data.map(() => 0) : [] }));
    }
    return [];
  };

  const processedData = useMemo(() => {
    const data = analyticsData || {};
    const { monthlyHourlyUsageTotalCount = {}, weeklyHourlyUsageTotalCount = {}, dayHourlyUsageTotalCount = {}, yearlyUsageTotalCount = {} } = data;
    const now = new Date();
    const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    const currentMonthAbbrev = new Intl.DateTimeFormat('tr-TR', { month: 'short' }).format(now);

    const segments = ['1-7', '8-14', '15-21', '22-rest'];
    const monthlyCategories = segments.map(seg => seg === '22-rest' ? `${currentMonthAbbrev} 22-${lastDayOfMonth}` : `${currentMonthAbbrev} ${seg}`);
    const monthlyUsageData = segments.map(seg => Object.values(monthlyHourlyUsageTotalCount[seg] || {}).reduce((s, slot) => s + (slot?.usageCount || 0), 0));
    const monthlyExtendData = segments.map(seg => Object.values(monthlyHourlyUsageTotalCount[seg] || {}).reduce((s, slot) => s + (slot?.extendCount || 0), 0));
    const monthlyRevenue = segments.map(seg => Object.values(monthlyHourlyUsageTotalCount[seg] || {}).reduce((s, slot) => s + (slot?.totalPrice || 0), 0));

    const monthlyUsageAndExtendChart = {
      series: [{ name: 'Kullanım Adedi', data: monthlyUsageData }, { name: 'Uzatma Adedi', data: monthlyExtendData }],
      options: { chart: { type: 'bar', stacked: true, toolbar: { show: false } }, plotOptions: { bar: { columnWidth: '35%', borderRadius: 8 } }, colors: ['#1C64F2', '#FDBA8C'], xaxis: { categories: monthlyCategories }, legend: { position: 'bottom' }, dataLabels: { enabled: false } },
    };
    const monthlyComboChart = {
      series: [{ name: 'Ciro', type: 'column', data: monthlyRevenue }, { name: 'Kullanım Adedi', type: 'line', data: monthlyUsageData }],
      options: {
        chart: { stacked: false, toolbar: { show: false } },
        stroke: { width: [0, 2] },
        plotOptions: { bar: { columnWidth: '20%' } },
        colors: ['#1C64F2', '#14B8A6'],
        xaxis: { categories: monthlyCategories },
        yaxis: [
          {
            title: { text: 'Ciro' },
            labels: {
              formatter: (val) => `${val.toFixed(0)} ₺`
            }
          },
          {
            opposite: true,
            title: { text: 'Kullanım Adedi' },
            labels: {
              formatter: (val) => val.toFixed(0)
            }
          }
        ],
        legend: { position: 'bottom' },
        tooltip: {
          y: {
            formatter: (val, { seriesIndex }) => {
              if (seriesIndex === 0) {
                return `${val.toFixed(2)} ₺`;
              }
              return `${val.toFixed(0)} adet`;
            }
          }
        }
      },
    };

    // Yearly Usage Chart - Yıllık Kullanım ve Ciro Hareketleri
    const yearlyUsage = Object.entries(yearlyUsageTotalCount || {})
      .sort((a, b) => a[0].localeCompare(b[0]))
      .reduce((acc, [monthKey, data]) => {
        const monthLabel = moment(monthKey, 'YYYY-MM').format('MMM YYYY');
        acc.categories.push(monthLabel);
        acc.usageData.push(data.usageCount || 0);
        acc.revenueData.push(data.totalPrice || 0);
        return acc;
      }, { categories: [], usageData: [], revenueData: [] });

    const yearlyUsageChart = {
      series: [
        { name: 'Ciro', type: 'column', data: yearlyUsage.revenueData },
        { name: 'Kullanım Adedi', type: 'line', data: yearlyUsage.usageData },
      ],
      options: {
        chart: { stacked: false, toolbar: { show: false } },
        stroke: { width: [0, 2] },
        plotOptions: { bar: { columnWidth: '40%' } },
        colors: ['#1C64F2', '#14B8A6'],
        xaxis: {
          categories: yearlyUsage.categories,
          labels: {
            rotate: -45,
            rotateAlways: false,
          }
        },
        yaxis: [
          {
            title: { text: 'Ciro' },
            labels: {
              formatter: (val) => `${val.toFixed(0)} ₺`
            }
          },
          {
            opposite: true,
            title: { text: 'Adet' },
            labels: {
              formatter: (val) => val.toFixed(0)
            }
          }
        ],
        legend: { position: 'bottom' },
        tooltip: {
          y: {
            formatter: (val, { seriesIndex }) => {
              if (seriesIndex === 0) {
                return `${val.toFixed(2)} ₺`;
              }
              return `${val.toFixed(0)} adet`;
            }
          }
        }
      }
    };

    const startOfWeek = moment().startOf('isoWeek');
    const weekDates = Array.from({ length: 7 }, (_, i) => startOfWeek.clone().add(i, 'days').format('YYYY-MM-DD'));
    const weeklyCategories = weekDates.map(date => moment(date).format('DD MMM'));
    const weeklyUsageData = weekDates.map(date => Object.values(weeklyHourlyUsageTotalCount[date] || {}).reduce((s, slot) => s + (slot?.usageCount || 0), 0));
    const weeklyExtendData = weekDates.map(date => Object.values(weeklyHourlyUsageTotalCount[date] || {}).reduce((s, slot) => s + (slot?.extendCount || 0), 0));
    const weeklyRevenue = weekDates.map(date => Object.values(weeklyHourlyUsageTotalCount[date] || {}).reduce((s, slot) => s + (slot?.totalPrice || 0), 0));

    const weeklyUsageAndExtendChart = {
      series: [{ name: 'Kullanım Adedi', data: weeklyUsageData }, { name: 'Uzatma Adedi', data: weeklyExtendData }],
      options: { chart: { type: 'bar', stacked: true, toolbar: { show: false } }, plotOptions: { bar: { columnWidth: '40%', borderRadius: 8 } }, colors: ['#1C64F2', '#FDBA8C'], xaxis: { categories: weeklyCategories }, legend: { position: 'bottom' }, dataLabels: { enabled: false } },
    };
    const weeklyComboChart = {
      series: [{ name: 'Ciro', type: 'column', data: weeklyRevenue }, { name: 'Kullanım Adedi', type: 'line', data: weeklyUsageData }],
      options: {
        chart: { stacked: false, toolbar: { show: false } },
        stroke: { width: [0, 2] },
        plotOptions: { bar: { columnWidth: '40%' } },
        colors: ['#1C64F2', '#14B8A6'],
        xaxis: { categories: weeklyCategories },
        yaxis: [
          {
            title: { text: 'Ciro' },
            labels: {
              formatter: (val) => `${val.toFixed(0)} ₺`
            }
          },
          {
            opposite: true,
            title: { text: 'Kullanım Adedi' },
            labels: {
              formatter: (val) => val.toFixed(0)
            }
          }
        ],
        legend: { position: 'bottom' },
        tooltip: {
          y: {
            formatter: (val, { seriesIndex }) => {
              if (seriesIndex === 0) {
                return `${val.toFixed(2)} ₺`;
              }
              return `${val.toFixed(0)} adet`;
            }
          }
        }
      },
    };

    const allTimeSlots = [...new Set(Object.values(dayHourlyUsageTotalCount).flatMap(day => (day && typeof day === 'object') ? Object.keys(day) : []))].sort();

    // Her saat için satır toplamlarını hesapla
    const hourlyTotals = allTimeSlots.map(timeSlot => {
      return DAY_ORDER.reduce((sum, dayKey) => sum + (dayHourlyUsageTotalCount[dayKey]?.[timeSlot]?.[heatmapDataType] || 0), 0);
    });

    // Her gün için sütun toplamlarını hesapla
    const dailyTotals = DAY_ORDER.map(dayKey => {
      return allTimeSlots.reduce((sum, timeSlot) => sum + (dayHourlyUsageTotalCount[dayKey]?.[timeSlot]?.[heatmapDataType] || 0), 0);
    });

    // Genel toplam
    const grandTotal = dailyTotals.reduce((sum, val) => sum + val, 0);

    // En alta günlük toplamlar satırı ekle
    const totalRow = {
      name: 'Toplam',
      data: [
        ...DAY_ORDER.map((dayKey, index) => ({ x: DAY_MAP_TR[dayKey], y: dailyTotals[index] })),
        { x: 'Toplam', y: grandTotal }
      ]
    };

    // Series oluştur - her satıra "Toplam" sütunu ekle
    const heatmapSeries = [
      totalRow,
      ...allTimeSlots.map((timeSlot, index) => ({
        name: timeSlot.split('-')[0],
        data: [
          ...DAY_ORDER.map(dayKey => ({ x: DAY_MAP_TR[dayKey], y: dayHourlyUsageTotalCount[dayKey]?.[timeSlot]?.[heatmapDataType] || 0 })),
          { x: 'Toplam', y: hourlyTotals[index] }
        ]
      })).reverse()
    ];

    const allCounts = Object.values(dayHourlyUsageTotalCount).flatMap(day => (day && typeof day === 'object') ? Object.values(day) : []).map(item => item[heatmapDataType] || 0);
    const maxCount = allCounts.length > 0 ? Math.max(...allCounts, 0) : 10;
    const usageColors = ['#D7D7D7', '#93D4F4', '#46A5F9', '#4086F5', '#345CC3'];
    const extendColors = ['#D7D7D7', '#89D6CE', '#54C4B8', '#14B8A6', '#188B7E'];
    const activeColors = heatmapDataType === 'usageCount' ? usageColors : extendColors;
    const dailyHeatmapChart = {
      series: heatmapSeries,
      options: {
        chart: { type: 'heatmap', toolbar: { show: false } },
        plotOptions: {
          heatmap: {
            shadeIntensity: 0.4,
            radius: 2,
            colorScale: {
              ranges: [
                { from: 0, to: 0, color: activeColors[0] },
                { from: 1, to: Math.floor(maxCount * 0.25), color: activeColors[2] },
                { from: Math.floor(maxCount * 0.25) + 1, to: Math.floor(maxCount * 0.7), color: activeColors[3] },
                { from: Math.floor(maxCount * 0.7) + 1, to: maxCount, color: activeColors[4] }
              ]
            }
          }
        },
        dataLabels: {
          enabled: true,
          style: {
            colors: ['#000'],
            fontSize: '12px',
            fontWeight: 500
          }
        },
        stroke: { show: true, width: 3, colors: ['#fff'] },
        xaxis: { type: 'category' },
        legend: { show: false },
        tooltip: { y: { formatter: (value) => `${value} adet` } }
      },
      maxCount,
      colors: activeColors,
    };

    return { monthlyUsageAndExtendChart, monthlyComboChart, yearlyUsageChart, weeklyUsageAndExtendChart, weeklyComboChart, dailyHeatmapChart };
  }, [analyticsData, heatmapDataType]);

  const filteredDropboxList = useMemo(() => dropboxList.filter(item => { const name = `${item.checkRoomMember?.name || ''} ${item.checkRoomMember?.surName || ''}`.trim(); return filterText === '' || name.toLowerCase().includes(filterText.toLowerCase()) || item.locationCode?.toLowerCase().includes(filterText.toLowerCase()) || item.checkRoomNumber?.toString().toLowerCase().includes(filterText.toLowerCase()); }), [dropboxList, filterText]);
  const paginatedDropboxList = useMemo(() => filteredDropboxList.slice((page - 1) * itemsPerPage, (page - 1) * itemsPerPage + itemsPerPage), [filteredDropboxList, page]);

  if (loading || permissionLoading) { return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}><CircularProgress /></Box>; }

  return (
    <Box sx={{ px: { xs: 2, sm: 4 }, py: 3 }}>
      <Helmet><title>Emanet - Vestiyer</title></Helmet>

      {showApplyBanner && !hasPermission && (
        <Stack sx={{ width: '100%', mb: 4, mx: 'auto', borderBottom: '1px solid #D6BBFB', borderRadius: 1, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)' }}>
          <Alert severity="info" sx={{ backgroundColor: 'white', alignItems: 'center', pr: 4, '& .MuiAlert-icon': { color: theme.palette.info.main } }} action={
            <Stack direction="row" gap={1} alignItems="center">
              <Button color="primary" variant="contained" size="small" onClick={handleApplyClick} sx={{ padding: '10px 16px', cursor: 'pointer' }}>Başvur</Button>
              <IconButton aria-label="close" color="inherit" size="small" sx={{ cursor: 'pointer' }} onClick={() => { setShowApplyBanner(false); }}><Iconify icon="eva:close-fill" width={20} height={20} /></IconButton>
            </Stack>
          } onClose={() => setShowApplyBanner(false)} >
            Bu hizmet şu anda aktif değil. Grafik ve liste verilerini görüntülemek için başvuruda bulunabilirsiniz.
          </Alert>
        </Stack>
      )}

      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3} flexWrap="wrap" gap={1}>
        <Typography variant="h5">Emanet - Vestiyer</Typography>
        <Button variant="text" startIcon={<Iconify icon="eva:play-circle-fill" />} sx={{ textTransform: 'none' }} onClick={handleOpenVideoModal}>Tanıtım Videosunu İzle</Button>
      </Stack>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card elevation={0} sx={{ display: 'flex', alignItems: 'center', p: 2, minHeight: 140, flex: 1, borderRadius: 2, bgcolor: 'background.paper' }}>
            <Box sx={{ flex: 1 }}><Typography variant="subtitle2">Aylık Kullanım Adedi</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Aylık Toplam: <strong>{analyticsData?.totalUsedBoxCount ?? 0}</strong></Typography>
            </Box>
            <Box sx={{ width: 40, height: 40, borderRadius: 1, background: 'linear-gradient(135deg,#3461D6 0%,#264697 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Iconify icon='eva:layers-outline' width={20} height={20} sx={{ color: '#fff' }} /></Box>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card elevation={0} sx={{ display: 'flex', alignItems: 'center', p: 2, minHeight: 140, flex: 1, borderRadius: 2, bgcolor: 'background.paper' }}>
            <Box sx={{ flex: 1 }}><Typography variant="subtitle2">Günlük Kullanım Adedi (Bugün)</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Toplam: <strong>{analyticsData?.totalDailyBoxCount ?? 0}</strong></Typography>
            </Box>
            <Box sx={{ width: 40, height: 40, borderRadius: 1, background: 'linear-gradient(135deg,#3461D6 0%,#264697 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Iconify icon='eva:layers-outline' width={20} height={20} sx={{ color: '#fff' }} /></Box>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card elevation={0} sx={{ p: 2, minHeight: 140, borderRadius: 2, bgcolor: 'background.paper', display: 'flex', flexDirection: 'column' }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{
                mb: priceInfo.length > 1 ? 0 : 2,
                flexShrink: 0
              }}
            >
              <Typography variant="subtitle2">
                Fiyat Bilgileri
              </Typography>

              {!loadingPrices && hasPermission && priceInfo.length > 0 && (
                <Chip
                  label={`${priceInfo.length} Lokasyon`}
                  size="small"
                  sx={{
                    fontSize: '11px',
                    height: '20px',
                    backgroundColor: (theme) => theme.palette.grey[200]
                  }}
                />
              )}
            </Stack>
            {loadingPrices && !loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
                <CircularProgress size={24} />
              </Box>
            )}
            {!loadingPrices && hasPermission && (
              <>
                {priceInfo.length > 1 && (
                  <Tabs
                    value={activePriceTab}
                    onChange={(e, newValue) => setActivePriceTab(newValue)}
                    variant="standard"
                    sx={{
                      borderBottom: 1,
                      borderColor: 'divider',
                      mb: 1,
                      minHeight: 'auto'
                    }}
                  >
                    {priceInfo.map((location, index) => (
                      <Tab
                        label={location.locationName || location.locationCode}
                        key={location.locationCode || index}
                        sx={{
                          textTransform: 'none',
                          minWidth: 'auto',
                          fontSize: '12px',
                          pb: 0,
                          px: 1.5,
                          pt: 0.5
                        }}
                      />
                    ))}
                  </Tabs>
                )}

                {priceInfo[activePriceTab] && priceInfo[activePriceTab].prices.length > 0 ? (
                  <Box sx={{
                    mt: priceInfo.length > 1 ? 0.5 : 0,
                    flexGrow: 1
                  }}>
                    <Box sx={{ display: 'flex', gap: 0.5, mb: 0.5 }}>
                      <Typography variant="body2" sx={{ minWidth: '45px', fontWeight: 600 }}>Saat:</Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, flex: 1 }}>
                        {priceInfo[activePriceTab].prices.map((p, i) => (
                          <Box key={i} sx={{ border: '1px solid #E0E0E0', borderRadius: 0.5, px: 1, py: 0.25, textAlign: 'center', flex: 1 }}>
                            <Typography variant="body2" sx={{ fontSize: '12px' }}>{p.time}</Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Typography variant="body2" sx={{ minWidth: '45px', fontWeight: 600 }}>Fiyat:</Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, flex: 1 }}>
                        {priceInfo[activePriceTab].prices.map((p, i) => (
                          <Box key={i} sx={{ border: '1px solid #E0E0E0', borderRadius: 0.5, px: 1, py: 0.25, textAlign: 'center', flex: 1 }}>
                            <Typography variant="body2" sx={{ fontSize: '12px' }}>{p.price}</Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1, minHeight: '60px' }}>
                    <Typography variant="body2" color="text.secondary">Aktif fiyat bulunamadı.</Typography>
                  </Box>
                )}
              </>
            )}

            {!loadingPrices && !hasPermission && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1, minHeight: '60px' }}>
                <Typography variant="body2" color="text.secondary">-</Typography>
              </Box>
            )}

          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2 }}><Typography variant="h6" gutterBottom>Aylık Toplam Kullanım ve Uzatmalar</Typography><Typography sx={{ color: '#4B5563', fontSize: '14px' }} variant="p" gutterBottom>Toplam Adet</Typography><ReactApexChart options={processedData.monthlyUsageAndExtendChart.options} series={hasPermission ? processedData.monthlyUsageAndExtendChart.series : zeroOutSeries(processedData.monthlyUsageAndExtendChart.series)} type="bar" height={260} /></Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2 }}><Typography variant="h6" gutterBottom>Aylık Toplam Kullanım ve Ciro Hareketleri</Typography><Typography sx={{ color: '#4B5563', fontSize: '14px' }} variant="p" gutterBottom>Toplam Kullanım Adedi - Toplam Ciro</Typography><ReactApexChart options={processedData.monthlyComboChart.options} series={hasPermission ? processedData.monthlyComboChart.series : zeroOutSeries(processedData.monthlyComboChart.series)} type="line" height={260} /></Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2 }}><Typography variant="h6" gutterBottom>Haftalık Toplam Kullanım ve Uzatmalar</Typography><Typography sx={{ color: '#4B5563', fontSize: '14px' }} variant="p" gutterBottom>Toplam Adet</Typography><ReactApexChart options={processedData.weeklyUsageAndExtendChart.options} series={hasPermission ? processedData.weeklyUsageAndExtendChart.series : zeroOutSeries(processedData.weeklyUsageAndExtendChart.series)} type="bar" height={260} /></Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2 }}><Typography variant="h6" gutterBottom>Haftalık Toplam Kullanım ve Ciro Hareketleri</Typography><Typography sx={{ color: '#4B5563', fontSize: '14px' }} variant="p" gutterBottom>Toplam Kullanım Adedi - Toplam Ciro</Typography><ReactApexChart options={processedData.weeklyComboChart.options} series={hasPermission ? processedData.weeklyComboChart.series : zeroOutSeries(processedData.weeklyComboChart.series)} type="line" height={260} /></Card>
        </Grid>
        <Grid item xs={12}>
          <Card elevation={0} sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom>Aylık Kullanım ve Ciro Hareketleri (Son 12 Ay)</Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '14px' }} variant="p" gutterBottom>Toplam Kullanım Adedi - Toplam Ciro</Typography>
            <ReactApexChart options={processedData.yearlyUsageChart.options} series={hasPermission ? processedData.yearlyUsageChart.series : zeroOutSeries(processedData.yearlyUsageChart.series)} type="line" height={260} />
          </Card>
        </Grid>
      </Grid>

      <Grid item xs={12} sx={{ mt: 2 }}>
        <Card elevation={0} sx={{ p: 2, height: 400, borderRadius: 2, bgcolor: 'background.paper' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
            <Box><Typography variant="h6">Gün ve Saat Bazlı Vestiyer Kullanım Hareketleri</Typography><Typography sx={{ color: '#4B5563', fontSize: '14px' }} variant="body2">Kullanım Yoğunluğu</Typography></Box>
            <FormControl size="small" sx={{ minWidth: 150 }}><InputLabel>Veri Tipi</InputLabel><Select value={heatmapDataType} label="Veri Tipi" onChange={(e) => setHeatmapDataType(e.target.value)}><MenuItem value="usageCount">Kullanım Adedi</MenuItem><MenuItem value="extendCount">Uzatma Adedi</MenuItem></Select></FormControl>
          </Stack>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box sx={{ flex: 1 }}><ReactApexChart options={processedData.dailyHeatmapChart.options} series={hasPermission ? processedData.dailyHeatmapChart.series : zeroOutSeries(processedData.dailyHeatmapChart.series)} type="heatmap" height={300} /></Box>
            <HeatmapLegend maxCount={processedData.dailyHeatmapChart.maxCount} colors={processedData.dailyHeatmapChart.colors} label={heatmapDataType === "usageCount" ? "Kullanım Adedi" : "Uzatma Adedi"} />
          </Stack>
        </Card>
      </Grid>

      <Paper sx={{ mt: 4, p: { xs: 2, sm: 3 }, bgcolor: 'background.paper', borderRadius: 2 }} elevation={0}>
        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={3}><TextField fullWidth placeholder="İsim, Lokasyon, Vestiyer No..." value={filterText} onChange={(e) => setFilterText(e.target.value)} InputProps={{ startAdornment: (<Iconify icon="eva:search-fill" sx={{ mr: 1 }} />) }} size="small" /></Grid>
          <Grid item xs={6} sm={3} md={2}><LocalizationProvider dateAdapter={AdapterMoment}><DatePicker label="Başlangıç Tarihi" value={startDate} onChange={setStartDate} renderInput={(params) => <TextField {...params} fullWidth size="small" />} /></LocalizationProvider></Grid>
          <Grid item xs={6} sm={3} md={2}><LocalizationProvider dateAdapter={AdapterMoment}><DatePicker label="Bitiş Tarihi" value={endDate} onChange={setEndDate} renderInput={(params) => <TextField {...params} fullWidth size="small" />} /></LocalizationProvider></Grid>
          <Grid item xs={12} sm={6} md={5}><Stack direction="row" spacing={2}><Button fullWidth variant="contained" size="medium" onClick={handleFilter} disabled={loadingList || !hasPermission}>{loadingList ? <CircularProgress size={24} /> : 'FİLTRELE'}</Button><Button fullWidth variant="outlined" color="secondary" size="medium" onClick={handleClearFilter} disabled={loadingList || !hasPermission}>TEMİZLE</Button><Button fullWidth variant="contained" size="medium" onClick={handleExportToExcel} disabled={!startDate || !endDate || filteredDropboxList.length === 0 || exporting || !hasPermission}>{exporting ? <CircularProgress size={24} color="inherit" /> : 'EXCEL\'E AKTAR'}</Button></Stack></Grid>
        </Grid>
        <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
          <Table>
            <TableHead sx={{ bgcolor: 'background.paper' }}><TableRow><TableCell>Lokasyon Kodu</TableCell><TableCell>Kullanım Süresi</TableCell><TableCell>Başlangıç Tarihi</TableCell><TableCell>Bitiş Tarihi</TableCell><TableCell>Vestiyer No</TableCell><TableCell>Ücret</TableCell></TableRow></TableHead>
            <TableBody>
              {paginatedDropboxList.length > 0 ? paginatedDropboxList.map((item, index) => (<TableRow key={`${item.id}-${index}`} hover><TableCell>{item.locationCode}</TableCell><TableCell>{calculateDuration(item.startDate, item.endDate)}</TableCell><TableCell>{item.startDate ? formatDate(item.startDate) : '-'}</TableCell><TableCell>{item.endDate ? formatDate(item.endDate) : '-'}</TableCell><TableCell>{item.checkRoomNumber || '-'}</TableCell><TableCell>{item.price !== undefined ? `${item.price} ₺` : '-'}</TableCell></TableRow>)) : (<TableRow><TableCell colSpan={6} align="center">{filterText || startDate || endDate ? 'Filtreleme sonucunda veri bulunamadı.' : 'Veri yok.'}</TableCell></TableRow>)}
            </TableBody>
            <TableFooter>
              <TableRow><TableCell colSpan={6} sx={{ py: 2 }}><Grid container alignItems="center" justifyContent="space-between"><Grid item><Typography variant="caption">Toplam {filteredDropboxList.length} kayıt</Typography></Grid>{Math.ceil(filteredDropboxList.length / itemsPerPage) > 1 && (<Grid item><Pagination count={Math.ceil(filteredDropboxList.length / itemsPerPage)} page={page} onChange={(e, val) => setPage(val)} shape="rounded" size="small" /></Grid>)}</Grid></TableCell></TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={isVideoModalOpen} onClose={handleCloseVideoModal} maxWidth="md" fullWidth>
        <DialogTitle sx={{ m: 0, p: 2 }}>Tanıtım Videosu<IconButton aria-label="close" onClick={handleCloseVideoModal} sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}><CloseIcon /></IconButton></DialogTitle>
        <DialogContent dividers><video width="100%" height="auto" controls autoPlay src="https://files.easypoint.com.tr/files/data-ekranlari/ep.mp4">Tarayıcınız video etiketini desteklemiyor.</video></DialogContent>
      </Dialog>

      <Dialog open={openApplyDialog} onClose={handleCloseDialog}>
        <DialogTitle>Hizmet Başvurusu</DialogTitle>
        <DialogContent><DialogContentText>Bu hizmetle ilgili bilgi alma isteğiniz Genel Merkezimize iletilecektir. Onaylıyor musunuz?</DialogContentText></DialogContent>
        <DialogActions><Button onClick={handleCloseDialog}>İptal</Button><Button onClick={handleConfirmApply} autoFocus>Onayla</Button></DialogActions>
      </Dialog>
    </Box>
  );
}